/**
 * @module @/app-config
 * @summary 通过 `ENV.confAPI` 拉取门户 App 配置（JSON），缓存与读取；替代 root 中单独的 `app` 字段
 * @author keep
 * @since 2026-05-19
 */

import { IAnyObject, isSuccess, JSONParse, JSONStringify, mergeWithArrayOverride } from '@yimoka/shared';
import { rootStore } from '@yimoka/store';

import { ENV, joinApiUrl } from '@/env';
import { getHostName, setTenantID } from '@/root';

const STORAGE_KEY = 'icua:appConfig';
const STORE_KEY = 'appConfig';

/**
 * App 远程配置（`confAPI` 门户 `byDomain` 返回的 JSON，字段可扩展）。
 */
export interface IAppConfig {
  id?: string;
  name?: string;
  title?: string;
  titleLocal?: string;
  logo?: string;
  favicon?: string;
  channel?: string;
  platform?: string;
  miniPlatform?: string;
  domain?: string;
  uiLib?: string;
  tenantID?: string;
  theme?: IAnyObject;
  layout?: IAnyObject;
  components?: IAnyObject;
  extra?: IAnyObject;
  extraLocal?: IAnyObject;
  [key: string]: unknown;
}

const defaultAppConfig: IAppConfig = {
  name: 'ICUA',
  title: 'ICUA',
};

/**
 * 将门户 `byDomain` 接口响应归一为 {@link IAppConfig}。
 *
 * @param body - 接口响应体
 * @returns 归一化后的配置
 */
export function normalizeAppConfig(body: IAnyObject): IAppConfig {
  const payload = (body?.data ?? body) as IAnyObject;
  const appData = (payload?.data ?? payload) as IAnyObject;
  const tenantID = (payload?.tenantID ?? appData?.tenantID) as string | undefined;

  return mergeWithArrayOverride(defaultAppConfig, {
    ...appData,
    tenantID,
  }) as IAppConfig;
}

/**
 * 写入内存并缓存到 localStorage。
 *
 * @param config - App 配置
 */
export function setAppConfig(config: IAppConfig): void {
  rootStore.setDataItem(STORE_KEY, config);
  try {
    localStorage.setItem(STORAGE_KEY, JSONStringify(config));
  } catch {
    // ignore
  }
  if (config.tenantID) {
    setTenantID(String(config.tenantID));
  }
}

/**
 * 读取当前 App 配置（内存优先，其次本地缓存）。
 *
 * @returns 配置对象，未加载时返回 undefined
 */
export function getAppConfig(): IAppConfig | undefined {
  const cached = rootStore.getDataItem(STORE_KEY);
  if (cached && typeof cached === 'object') {
    return cached as IAppConfig;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSONParse(raw) as IAppConfig;
      if (parsed && typeof parsed === 'object') {
        setAppConfig(parsed);
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return undefined;
}

/**
 * 当前 App 配置的应用 ID（用于 `x-md-global-app`）。
 *
 * @returns 应用 id 或空串
 */
export function getAppConfigId(): string {
  const id = getAppConfig()?.id;
  return id ? String(id) : '';
}

export const useAppConfig = () => getAppConfig();

/**
 * 通过 `ENV.confAPI` 拉取 App 配置（`GET .../shared/frontend/portal/app/byDomain`）。
 *
 * @returns 合并后的配置；未配置 confAPI 或失败时使用默认配置
 */
export async function fetchAppConfig(): Promise<IAppConfig> {
  try {
    if (ENV.confAPI) {
      const apiPath = joinApiUrl(ENV.confAPI, '/shared/frontend/portal/app/byDomain');
      const url = apiPath.startsWith('http://') || apiPath.startsWith('https://')
        ? new URL(apiPath)
        : new URL(apiPath, window.location.origin);
      url.searchParams.set('value', getHostName());
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`confAPI HTTP ${res.status}`);
      }
      const json = (await res.json()) as IAnyObject;
      if (typeof json.code === 'number' && !isSuccess(json as { code: number })) {
        throw new Error(json.msg as string ?? '门户配置接口失败');
      }
      const config = normalizeAppConfig(json);
      setAppConfig(config);
      return config;
    }
  } catch (error) {
    if (ENV.debug) {
      console.warn('[app-config] 远程配置加载失败，使用默认配置', error);
    }
  }

  const fallback = mergeWithArrayOverride(defaultAppConfig, {}) as IAppConfig;
  setAppConfig(fallback);
  return fallback;
}

/**
 * 是否配置了门户 confAPI（有则启动时拉取 App 配置）。
 *
 * @returns 是否应尝试拉取
 */
export function shouldFetchAppConfig(): boolean {
  return Boolean(ENV.confAPI);
}
